import React from 'react'
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch, useWatch } from 'react-hook-form'
import { t } from 'ttag'
import { FormData } from '../../ChallengeAdmin'
import { activityTypes } from '../../activityTypes'
import { FormGroup, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import YumTooltip from '../../../../components/YumTooltip/YumTooltip'
import YumFormSwitch from '../../../../components/YumFormSwitch/YumFormSwitch'
import { CustomTypeCategory, customUnits } from '../../../../services/CustomTypes'
import { DivisionCategoryRow } from '../../../../services/api.types'

import { ReactComponent as ChatActiveIcon } from '../../../../assets/v3/icons/chat-active.svg'
import { ReactComponent as ChatInactiveIcon } from '../../../../assets/v3/icons/chat-inactive.svg'

import { ReactComponent as ManualActiveIcon } from '../../../../assets/v3/icons/manual-active.svg'
import { ReactComponent as ManualInactiveIcon } from '../../../../assets/v3/icons/manual-inactive.svg'

import { ReactComponent as RoundDailyPointsActiveIcon } from '../../../../assets/v3/icons/round-daily-points-active.svg'
import { ReactComponent as RoundDailyPointsInactiveIcon } from '../../../../assets/v3/icons/round-daily-points-inactive.svg'

import { ReactComponent as AutoJoinUsersActiveIcon } from '../../../../assets/v3/icons/auto-join-users-active.svg'
import { ReactComponent as AutoJoinUsersInactiveIcon } from '../../../../assets/v3/icons/auto-join-users-inactive.svg'

import { ReactComponent as SimpleManualEntryInactiveIcon } from '../../../../assets/v3/icons/simple-manual-entry-inactive.svg'
import { ReactComponent as GroupInactiveIcon } from '../../../../assets/v3/icons/group-inactive.svg'
import { ReactComponent as IndividualInactiveIcon } from '../../../../assets/v3/icons/individual-inactive.svg'

import { UserCompanyDto } from '../../../../services/api.types'
import ChallengeMaxManualEntryDays from '../ChallengeMaxManualEntryDays/ChallengeMaxManualEntryDays'
import { isSimpleDayType, DIVISION_CATEGORY_PREFIX } from '../../../../common/helpers/challenges'
import { ActivityView } from '../../../Activity/store/ActivityReducer'
import { hasCompanyFeatureFlag } from '../../../../common/helpers/featureFlags'
import DailyMaxField from '../ChallengeStepGoal/fields/DailyMaxField'
import { isDailyMaxEnabled as isDailyMaxSupported } from '../ChallengeStepGoal/goalValidationHelpers'
import { useTotalGoalEndAdornment } from '../ChallengeStepGoal/goalUnitHelpers'
import YumMediumTitle from '../../../../components/YumMediumTitle/YumMediumTitle'

interface ChallengeStepPeriodProps {
  register: UseFormRegister<FormData>
  control: Control<FormData>
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  activityType: CustomTypeCategory
  isUserCreatedChallenge: boolean
  userCompanies: UserCompanyDto[]
  formStep: number
}

export default function ChallengeStepConfiguration(props: ChallengeStepPeriodProps) {
  const { register, control, errors, watch, setValue, activityType, isUserCreatedChallenge, userCompanies, formStep } =
    props

  const chatEnabled = watch('chatEnabled')
  const manualEntryEnabled = watch('manualEntryEnabled')
  const roundDailyPoints = watch('roundDailyPoints')
  const autoJoinEnabled = watch('autoJoinEnabled')
  const simpleManualEntry = watch('simpleManualEntry')
  const goalType = watch('goalType')
  const showGroupMembers = watch('showGroupMembers')
  const showGroupMembersContributions = watch('showGroupMembersContributions')
  const hideLeaderboardTables = watch('hideLeaderboardTables')

  // Watch custom unit and type for daily max field end adornment
  const customType = useWatch({ control, name: 'customType' })
  const customUnit = useWatch({ control, name: 'customUnit' })
  const customUnitFieldWithText = customUnits.find((cu) => cu.id === customType)?.text
  const dailyMaxEndAdornment = useTotalGoalEndAdornment(activityType, customUnit ?? '', customUnitFieldWithText)

  // Check if daily goal (daily max) should be shown
  const dailyMaxSupported = isDailyMaxSupported(formStep, activityType, goalType, simpleManualEntry)

  const showManualEntryOption =
    !!activityTypes.find(({ value, usesIntegration }) => value === activityType && usesIntegration) &&
    activityType !== CustomTypeCategory.TOTAL_POINTS
  const showRoundDailyPointsOption = activityType === CustomTypeCategory.POINTS
  const companyId = watch('companyId') as string
  const showAutoJoinOption = hasCompanyFeatureFlag(userCompanies, companyId, 'challengeAutoJoinOption')
  const leaderboards: string[] = watch('leaderboards') || []
  const hasGroupLeaderboard = leaderboards.some(
    (id) => id === ActivityView.GROUP || id.startsWith(DIVISION_CATEGORY_PREFIX),
  )

  const showHideLeaderboardTablesOption = hasCompanyFeatureFlag(
    userCompanies,
    companyId,
    'allowHidingLeaderboardTables',
  )

  // Focusable button so keyboard users can reach the tooltip (it opens on focus,
  // WCAG 2.1.1); stopPropagation keeps it from toggling the parent ListItem.
  const infoIcon = (tooltip: string) => (
    <YumTooltip title={tooltip} arrow placement="top">
      <button
        type="button"
        aria-label={tooltip}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        style={{
          background: 'none',
          border: 0,
          padding: 0,
          marginLeft: 6,
          display: 'inline-flex',
          verticalAlign: 'text-bottom',
          cursor: 'help',
        }}
      >
        <InfoOutlined aria-hidden="true" style={{ width: 16, height: 16, color: '#707173' }} />
      </button>
    </YumTooltip>
  )

  const simpleManualEntryField = (
    <FormGroup>
      <ListItem role={undefined} dense button onClick={() => setValue('simpleManualEntry', !simpleManualEntry)}>
        <ListItemIcon>
          {/*{simpleManualEntry ? <SimpleManualEntryActiveIcon /> : <SimpleManualEntryInactiveIcon />}*/}
          <SimpleManualEntryInactiveIcon />
        </ListItemIcon>
        <ListItemText
          primary={
            <>
              {t`One-click manual entry`}
              {infoIcon(t`Simplify manual entry to a single tap — participants just confirm the day is done.`)}
            </>
          }
        />
        <ListItemIcon>
          <YumFormSwitch control={control} name="simpleManualEntry" />
        </ListItemIcon>
      </ListItem>
    </FormGroup>
  )

  return (
    <>
      <FormGroup>
        <ListItem role={undefined} dense button onClick={() => setValue('chatEnabled', !chatEnabled)}>
          <ListItemIcon>
            {/*{chatEnabled ? <ChatActiveIcon /> : <ChatInactiveIcon />}*/}
            <ChatInactiveIcon />
          </ListItemIcon>
          <ListItemText
            primary={
              <>
                {t`Challenge chat`}
                {infoIcon(t`Enable a chat room for participants to communicate during the challenge.`)}
              </>
            }
          />
          <ListItemIcon>
            <YumFormSwitch control={control} name="chatEnabled" />
          </ListItemIcon>
        </ListItem>
        {showAutoJoinOption && (
          <ListItem role={undefined} dense button onClick={() => setValue('autoJoinEnabled', !autoJoinEnabled)}>
            <ListItemIcon>
              {/*{autoJoinEnabled ? <AutoJoinUsersActiveIcon /> : <AutoJoinUsersInactiveIcon />}*/}
              <AutoJoinUsersInactiveIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  {t`Auto-join users`}
                  {infoIcon(
                    t`Automatically add all company users to this challenge. New users will also be joined when they sign up.`,
                  )}
                </>
              }
            />
            <ListItemIcon>
              <YumFormSwitch control={control} name="autoJoinEnabled" />
            </ListItemIcon>
          </ListItem>
        )}
        {hasGroupLeaderboard && (
          <ListItem role={undefined} dense button onClick={() => setValue('showGroupMembers', !showGroupMembers)}>
            <ListItemIcon>
              <GroupInactiveIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  {t`Show group participants`}
                  {infoIcon(t`Allow viewing individual team/group participants on the leaderboard.`)}
                </>
              }
            />
            <ListItemIcon>
              <YumFormSwitch control={control} name="showGroupMembers" />
            </ListItemIcon>
          </ListItem>
        )}
        {hasGroupLeaderboard && showGroupMembers && (
          <ListItem
            role={undefined}
            dense
            button
            onClick={() => setValue('showGroupMembersContributions', !showGroupMembersContributions)}
          >
            <ListItemIcon>
              <IndividualInactiveIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  {t`Show group participants contributions`}
                  {infoIcon(t`Show the individual contributions of team/group participants on the leaderboard.`)}
                </>
              }
            />
            <ListItemIcon>
              <YumFormSwitch control={control} name="showGroupMembersContributions" />
            </ListItemIcon>
          </ListItem>
        )}
        {showHideLeaderboardTablesOption && (
          <ListItem
            role={undefined}
            dense
            button
            onClick={() => setValue('hideLeaderboardTables', !hideLeaderboardTables)}
          >
            <ListItemIcon>
              <GroupInactiveIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  {t`Hide leaderboard tables`}
                  {infoIcon(t`Keep the leaderboard views visible but hide the ranking tables on them.`)}
                </>
              }
            />
            <ListItemIcon>
              <YumFormSwitch control={control} name="hideLeaderboardTables" />
            </ListItemIcon>
          </ListItem>
        )}
        {showRoundDailyPointsOption && (
          <ListItem role={undefined} dense button onClick={() => setValue('roundDailyPoints', !roundDailyPoints)}>
            <ListItemIcon>
              {/*{roundDailyPoints ? <RoundDailyPointsActiveIcon /> : <RoundDailyPointsInactiveIcon />}*/}
              <RoundDailyPointsInactiveIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  {t`Rounding of daily points`}
                  {infoIcon(t`Round daily point values to the nearest whole number.`)}
                </>
              }
            />
            <ListItemIcon>
              <YumFormSwitch control={control} name="roundDailyPoints" />
            </ListItemIcon>
          </ListItem>
        )}
        {showManualEntryOption && (
          <>
            <ListItem role={undefined} dense button onClick={() => setValue('manualEntryEnabled', !manualEntryEnabled)}>
              <ListItemIcon>
                {/*{manualEntryEnabled ? <ManualActiveIcon /> : <ManualInactiveIcon />}*/}
                <ManualInactiveIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    {t`Manual entry`}
                    {infoIcon(
                      t`Allow participants to manually log activity data that is normally synced automatically (e.g. steps, distance). Activity types that can only be entered manually are always allowed regardless of this setting.`,
                    )}
                  </>
                }
              />
              <ListItemIcon>
                <YumFormSwitch control={control} name="manualEntryEnabled" />
              </ListItemIcon>
            </ListItem>
          </>
        )}
        {(manualEntryEnabled &&
          goalType === 'consistentDays' &&
          !isSimpleDayType(activityType as CustomTypeCategory) &&
          activityType !== CustomTypeCategory.ALL &&
          activityType !== CustomTypeCategory.POINTS) ||
        activityType === CustomTypeCategory.CUSTOM
          ? simpleManualEntryField
          : null}
        {manualEntryEnabled && <ChallengeMaxManualEntryDays control={control} />}

        {/* Daily max field */}
        {dailyMaxSupported && (
          <>
            <YumMediumTitle>{t`Best practice`}</YumMediumTitle>
            <DailyMaxField
              control={control}
              fieldPrefix=""
              validationRules={{
                min: dailyMaxSupported ? 1 : undefined,
                max: dailyMaxSupported ? 1000000 : undefined,
              }}
              endAdornment={dailyMaxEndAdornment}
              noTopMargin
            />
          </>
        )}
      </FormGroup>
    </>
  )
}
